import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  Wallet,
  Play,
  Pause,
  ArrowRight,
  Truck,
  MapPin,
  SlidersHorizontal,
  Globe2,
} from "lucide-react";
import beAMerchantImg from "../../assets/be-a-merchant.png";
import beASellerImg from "../../assets/be-a-seller.png";
import chefBannerKidImg from "../../assets/chef-banner-kid-v2.png";
import supportAgentImg from "../../assets/support-agent.png";
import homeKitchenImg from "../../assets/home-kitchen.jpg";
import foodVarietyImg from "../../assets/food-variety.jpg";
import "./AboutUs.css";

// ─── Motion presets ──────────────────────────────────────────────────────────

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

/** For grids: pass `custom={index}` on each child to stagger it. */
const staggerUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: (i) => ({ duration: 0.5, delay: i * 0.09, ease: [0.16, 1, 0.3, 1] }),
};

// ─── Static content — every claim maps to something real in the app ────────

/** What the hero headline rotates through — OwnFood's actual verticals. */
const HERO_WORDS = ["HOME COOKED.", "BIRYANI.", "CATERING.", "RECIPES."];

/** Chef benefit cards — 4 tall solid-color tiles with a badge icon, colored heading, and muted description. */
const chefBenefits = [
  { title: "You Keep Control", desc: "Set your own menu, prices, and hours — nobody manages your kitchen but you.", tint: "maroon", icon: SlidersHorizontal },
  { title: "We Do The Heavy Lifting", desc: "Ordering, payments, and customer support are on us. You just cook.", tint: "teal", icon: Truck },
  { title: "Free To Join", desc: "There's no fee to register a kitchen. If you don't sell, you don't pay us anything.", tint: "charcoal", icon: Wallet },
  { title: "Grow Beyond Your Street", desc: "Get discovered by food lovers across your city, not just your neighbors.", tint: "green", icon: Globe2 },
];

export default function AboutUs() {
  const [wordIndex, setWordIndex] = useState(0);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = setInterval(() => {
      setWordIndex((i) => (i + 1) % HERO_WORDS.length);
    }, 2600);
    return () => clearInterval(timer);
  }, [playing]);

  return (
    <div className="page-navbar-space about-page">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="about-hero">
        <div className="container about-hero-inner">
          <motion.span
            className="about-hero-eyebrow"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Bangladesh's Home-Kitchen Marketplace
          </motion.span>

          <motion.h1
            className="about-hero-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="about-hero-rotator">
              <AnimatePresence mode="wait">
                <motion.span
                  key={HERO_WORDS[wordIndex]}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="about-hero-rotator-word"
                >
                  {HERO_WORDS[wordIndex]}
                </motion.span>
              </AnimatePresence>
            </span>
            <br />
            DELIVERED.
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.22 }}
          >
            <Link to="/all-food" className="about-hero-search" aria-label="Browse kitchens near you">
              <MapPin size={16} className="about-hero-search-icon" />
              <span className="about-hero-search-text">Enter your delivery address</span>
              <span className="about-hero-search-btn" aria-hidden="true">
                <ArrowRight size={16} />
              </span>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Link to="/authentication/login" className="about-hero-pill-link">
              Log in for saved addresses
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.38 }}
          >
            <Link to="/all-kitchens" className="about-hero-popular">
              Popular home kitchens near you <ArrowRight size={12} />
            </Link>
          </motion.div>
        </div>

        <button
          type="button"
          className="about-hero-pause"
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? "Pause rotating headline" : "Play rotating headline"}
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>

        <svg
          className="about-hero-curve"
          viewBox="0 0 1440 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d="M0,0 Q720,130 1440,0 L1440,100 L0,100 Z" fill="#f6f8fb" />
        </svg>
      </section>

      {/* Everything below the hero sits on one continuous light canvas, laid out the same way wolt.com structures its dark one */}
      <div className="about-canvas">
        {/* ── Two-up: What OwnFood is / how it helps (promotional, not CTAs) ── */}
        <section className="about-section-tight">
          <div className="container">
            <div className="about-photo-grid">
              <motion.div className="about-photo-card" {...fadeUp}>
                <img
                  src={homeKitchenImg}
                  alt="A bright, well-equipped home kitchen"
                  className="about-photo-card-img"
                />
                <div className="about-photo-card-scrim" />
                <div className="about-photo-card-body">
                  <h3>A Home-Kitchen Marketplace</h3>
                  <p>OwnFood connects home cooks and small food businesses with people craving authentic, homemade meals — all in one place.</p>
                </div>
              </motion.div>

              <motion.div className="about-photo-card" {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
                <img
                  src={foodVarietyImg}
                  alt="A variety of homemade dishes ready to order"
                  className="about-photo-card-img"
                />
                <div className="about-photo-card-scrim" />
                <div className="about-photo-card-body">
                  <h3>Everything In One Platform</h3>
                  <p>Ordering, delivery, payments, catering, and a recipe marketplace — OwnFood brings it all together so home chefs can focus on cooking.</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── Statement band ───────────────────────────────────────────── */}
        <section className="about-statement">
          <div className="container">
            <motion.h2 className="about-statement-heading" {...fadeUp}>
              REAL KITCHENS. REAL PEOPLE. REAL FOOD.
            </motion.h2>
            <motion.p
              className="about-statement-sub"
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
            >
              There has never been a dedicated platform for home-cooked food
              sellers — until now. No more relying on Facebook groups,
              Messenger, or manual deliveries.
            </motion.p>
          </div>
        </section>

        {/* ── Two-up: Support / Tracking ────────────────────────────────── */}
        <section className="about-section-tight">
          <div className="container">
            <div className="about-photo-grid">
              <motion.div className="about-photo-card" custom={0} {...staggerUp}>
                <img
                  src={supportAgentImg}
                  alt="A support agent ready to help over a headset"
                  className="about-photo-card-img"
                />
                <div className="about-photo-card-scrim" />
                <div className="about-photo-card-body">
                  <h3>Real Support, From Real People</h3>
                  <p>Raise a support ticket any time and hear back from a real person — not a bot.</p>
                </div>
              </motion.div>

              <motion.div className="about-photo-card about-photo-card--tint" custom={1} {...staggerUp}>
                <img
                  src="https://images.unsplash.com/photo-1565299585323-38d6b0865b47?auto=format&fit=crop&w=900&q=80"
                  alt="A delivery bag ready to go out"
                  className="about-photo-card-img"
                />
                <div className="about-photo-card-scrim about-photo-card-scrim--indigo" />
                <div className="about-photo-card-body">
                  <h3>Live Order Tracking, Every Time</h3>
                  <p>Watch your order move from the kitchen to your door, from placed to delivered.</p>
                </div>
                <span className="about-badge-corner">Live Tracking</span>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── Full-width chef banner ─────────────────────────────────────── */}
        <section className="about-section-tight">
          <div className="container">
            <motion.div className="about-banner about-banner--amber" {...fadeUp}>
              <div className="about-banner-text about-banner-text--center">
                <span className="about-banner-kicker about-banner-kicker--plain">For Home Chefs</span>
                <h2 className="about-banner-heading">LET'S GROW YOUR KITCHEN TOGETHER</h2>
                <a
                  href="https://dashboard.ownfood.com/login"
                  target="_blank"
                  rel="noreferrer"
                  className="pill-btn pill-btn--tinted"
                >
                  Get started <ArrowRight size={16} />
                </a>
              </div>
              <img
                src={chefBannerKidImg}
                alt="A young chef holding a spoon and a whisk"
                className="about-banner-photo about-banner-photo--cutout"
              />
            </motion.div>
          </div>
        </section>

        {/* ── Four-up chef benefits ─────────────────────────────────────── */}
        <section className="about-section-tight">
          <div className="container">
            <div className="about-benefits-grid">
              {chefBenefits.map((b, i) => (
                <motion.div
                  className={`about-benefit-card about-benefit-card--${b.tint}`}
                  key={b.title}
                  custom={i}
                  {...staggerUp}
                >
                  <span className="about-benefit-card-badge">
                    <b.icon size={30} />
                  </span>
                  <h4 className="about-benefit-card-heading">{b.title}</h4>
                  <p>{b.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Full-width merchant banner ─────────────────────────────────── */}
        <section id="merchant" className="about-section-tight">
          <div className="container">
            <motion.div className="about-banner about-banner--dark" {...fadeUp}>
              <img
                src="https://images.unsplash.com/photo-1556911261-6bd341186b2f?auto=format&fit=crop&w=1200&q=80"
                alt="Two people cooking and enjoying themselves together in a kitchen"
                className="about-banner-bg"
              />
              <div className="about-banner-overlay" />
              <div className="about-banner-centered">
                <span className="about-banner-kicker">For Sellers &amp; Merchants</span>
                <h2 className="about-banner-heading">EARN WITH OWNFOOD, YOUR WAY</h2>
                <a
                  href="https://dashboard.ownfood.com/login"
                  target="_blank"
                  rel="noreferrer"
                  className="pill-btn pill-btn--dark"
                >
                  Get started <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── Two-up merchant benefits ─────────────────────────────────── */}
        <section className="about-section-tight">
          <div className="container">
            <div className="about-photo-grid">
              <motion.div className="about-photo-card about-photo-card--split" custom={0} {...staggerUp}>
                <img
                  src={beAMerchantImg}
                  alt="A merchant celebrating their earnings"
                  className="about-photo-card-img"
                />
                <div className="about-photo-card-scrim" />
                <div className="about-photo-card-body about-photo-card-body--split">
                  <div className="about-photo-card-text">
                    <h3>Be a Merchant</h3>
                    <p>৳100 for every home chef you bring on who gets approved — a one-time bounty, no cap.</p>
                  </div>
                  <span className="about-photo-card-btn">
                    Apply <ArrowRight size={13} />
                  </span>
                </div>
                <Link
                  to="/become-merchant"
                  className="about-photo-card-hit"
                  aria-label="Be a Merchant"
                />
              </motion.div>

              <motion.div className="about-photo-card about-photo-card--split" custom={1} {...staggerUp}>
                <img
                  src={beASellerImg}
                  alt="A home seller cooking in their kitchen"
                  className="about-photo-card-img"
                />
                <div className="about-photo-card-scrim" />
                <div className="about-photo-card-body about-photo-card-body--split">
                  <div className="about-photo-card-text">
                    <h3>Be a Seller</h3>
                    <p>You never have to cook. ৳0 cost to join, and approvals average just 2 days.</p>
                  </div>
                  <span className="about-photo-card-btn">
                    Apply <ArrowRight size={13} />
                  </span>
                </div>
                <Link
                  to="/become-seller"
                  className="about-photo-card-hit"
                  aria-label="Be a Seller"
                />
              </motion.div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
